/**
 * Landing Page V3 - Baseada no HTML com imagens Imagen 4
 * Adaptada para tema Gaming Dark com variações A/B
 */
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowRight,
  Check,
  Users,
  Star,
  Play,
  ChevronDown,
  Zap,
  Trophy,
  Target,
  Brain,
  Shield,
  Clock,
  Frown,
  AlertTriangle,
  Smartphone,
  Heart,
} from 'lucide-react'
import { LogoInline } from './brand/Logo'
import { Button } from './ui/button'
// import FloatingCTA from './landing/FloatingCTA' // FIXME: Componente não existe

// ============================================
// VARIAÇÕES A/B - HEADLINES E CTAs
// ============================================
// Variações principais + 3 variações orquestradas da C (neuro-UX)
export type LandingVariant = 'A' | 'B' | 'C' | 'C1' | 'C2' | 'C3' | 'D' | 'E'

// Estratégias de animação (neuro-UX orchestration)
type AnimationStrategy = 'default' | 'sequential' | 'focused' | 'reveal'

interface VariantContent {
  hero: {
    headline: React.ReactNode
    subtitle: string
    cta: string
    image: string // Imagem específica da variante
    animationStrategy: AnimationStrategy // Estratégia de orquestração visual
  }
  problem: {
    title: React.ReactNode
    subtitle: string
  }
  solution: {
    title: React.ReactNode
    subtitle: string
  }
  finalCta: {
    title: React.ReactNode
    subtitle: string
    cta: string
  }
}

const variants: Record<LandingVariant, VariantContent> = {
  // A: Anti-estudo (default - evita gatilho "estudar")
  A: {
    hero: {
      headline: 'O sistema que te guia até a aprovação',
      subtitle: 'Você só responde. O sistema te leva até lá. Sem decoreba. Sem livros. Só você e seu progresso.',
      cta: 'COMEÇAR GRÁTIS',
      image: '/hero-variants/hero-variant-a.webp', // Pessoa relaxada jogando celular
      animationStrategy: 'focused', // Nota 100: só CTA pulsa
    },
    problem: {
      title: 'O problema não é você. É o método.',
      subtitle: 'A maioria das pessoas não reprova por falta de capacidade. Reprova por falta de um sistema que funciona.',
    },
    solution: {
      title: 'O Loop de Progresso que você não consegue parar',
      subtitle: 'O CNH PASS usa ciência cognitiva para criar um ciclo que você quer continuar.',
    },
    finalCta: {
      title: 'Pronto pra conquistar sua carteira?',
      subtitle: '50.000+ pessoas já conquistaram a CNH com nosso método. Sua jornada começa com um clique.',
      cta: 'COMEÇAR GRÁTIS AGORA',
    },
  },

  // B: Aspiracional (liberdade)
  B: {
    hero: {
      headline: 'Sua liberdade sobre rodas começa aqui',
      subtitle: 'Imagina pegar a chave do seu primeiro veículo. Ir onde quiser, quando quiser. Em 30 dias você pode.',
      cta: 'COMEÇAR GRÁTIS',
      image: '/hero-variants/hero-variant-b.webp', // Pessoa com chave do carro
      animationStrategy: 'focused',
    },
    problem: {
      title: 'Por que tantos ficam presos no banco de trás?',
      subtitle: 'Não é falta de vontade. É um método que não funciona para a vida real.',
    },
    solution: {
      title: 'O caminho mais rápido até o volante',
      subtitle: 'Sistema inteligente que te prepara enquanto você vive sua vida.',
    },
    finalCta: {
      title: 'Daqui a 30 dias, onde você quer estar?',
      subtitle: 'Com a chave na mão ou ainda dependendo de carona? A escolha é sua.',
      cta: 'COMEÇAR GRÁTIS',
    },
  },

  // C: Gaming puro
  C: {
    hero: {
      headline: 'Jogue. Suba de nível. Conquiste sua CNH.',
      subtitle: 'Ganhe XP, mantenha seu streak, desbloqueie conquistas. Você nem percebe que está se preparando.',
      cta: 'JOGAR GRÁTIS',
      image: '/hero-variants/hero-variant-c.webp', // Gamer jogando
      animationStrategy: 'focused',
    },
    problem: {
      title: 'Decorar é chato. Jogar não.',
      subtitle: 'Por isso transformamos a preparação para CNH em um jogo que você vai querer jogar.',
    },
    solution: {
      title: 'O jogo que te aprova',
      subtitle: 'XP, streaks, conquistas e ranking. Tudo que você precisa para viciar positivamente.',
    },
    finalCta: {
      title: 'Pronto para a próxima fase?',
      subtitle: 'Milhares de jogadores já completaram. Agora é sua vez.',
      cta: 'JOGAR GRÁTIS',
    },
  },

  // ============================================
  // VARIAÇÕES ORQUESTRADAS DA C (Neuro-UX)
  // Baseado em Ch. 42-43 (Attention Cues, Multitasking)
  // ============================================

  // C1: Sequência Temporal - Animações em cascata, não simultâneas
  // Princípio: Task-switching has costs. Uma coisa de cada vez.
  C1: {
    hero: {
      headline: 'Jogue. Suba de nível. Conquiste sua CNH.',
      subtitle: 'Ganhe XP, mantenha seu streak, desbloqueie conquistas. Você nem percebe que está se preparando.',
      cta: 'JOGAR GRÁTIS',
      image: '/hero-variants/hero-variant-c.webp',
      animationStrategy: 'focused', // Nota 100: só CTA pulsa
    },
    problem: {
      title: 'Decorar é chato. Jogar não.',
      subtitle: 'Por isso transformamos a preparação para CNH em um jogo que você vai querer jogar.',
    },
    solution: {
      title: 'O jogo que te aprova',
      subtitle: 'XP, streaks, conquistas e ranking. Tudo que você precisa para viciar positivamente.',
    },
    finalCta: {
      title: 'Pronto para a próxima fase?',
      subtitle: 'Milhares de jogadores já completaram. Agora é sua vez.',
      cta: 'JOGAR GRÁTIS',
    },
  },

  // C2: Hierarquia Focalizada - Apenas CTA pulsa, badges estáticos
  // Princípio: Use attention-grabbers sparingly and strategically
  C2: {
    hero: {
      headline: 'Jogue. Suba de nível. Conquiste sua CNH.',
      subtitle: 'Ganhe XP, mantenha seu streak, desbloqueie conquistas. Você nem percebe que está se preparando.',
      cta: 'JOGAR GRÁTIS',
      image: '/hero-variants/hero-variant-c.webp',
      animationStrategy: 'focused', // Nota 100: só CTA pulsa
    },
    problem: {
      title: 'Decorar é chato. Jogar não.',
      subtitle: 'Por isso transformamos a preparação para CNH em um jogo que você vai querer jogar.',
    },
    solution: {
      title: 'O jogo que te aprova',
      subtitle: 'XP, streaks, conquistas e ranking. Tudo que você precisa para viciar positivamente.',
    },
    finalCta: {
      title: 'Pronto para a próxima fase?',
      subtitle: 'Milhares de jogadores já completaram. Agora é sua vez.',
      cta: 'JOGAR GRÁTIS',
    },
  },

  // C3: Reveal por Engajamento - Badges aparecem após delay significativo
  // Princípio: Anticipation more powerful than reward (dopamine seeking)
  C3: {
    hero: {
      headline: 'Jogue. Suba de nível. Conquiste sua CNH.',
      subtitle: 'Ganhe XP, mantenha seu streak, desbloqueie conquistas. Você nem percebe que está se preparando.',
      cta: 'JOGAR GRÁTIS',
      image: '/hero-variants/hero-variant-c.webp',
      animationStrategy: 'focused', // Nota 100: só CTA pulsa
    },
    problem: {
      title: 'Decorar é chato. Jogar não.',
      subtitle: 'Por isso transformamos a preparação para CNH em um jogo que você vai querer jogar.',
    },
    solution: {
      title: 'O jogo que te aprova',
      subtitle: 'XP, streaks, conquistas e ranking. Tudo que você precisa para viciar positivamente.',
    },
    finalCta: {
      title: 'Pronto para a próxima fase?',
      subtitle: 'Milhares de jogadores já completaram. Agora é sua vez.',
      cta: 'JOGAR GRÁTIS',
    },
  },

  // D: Provocativo
  D: {
    hero: {
      headline: 'Quem disse que precisa decorar?',
      subtitle: '50.000+ pessoas já passaram com nosso sistema. O segredo? Ele faz o trabalho pesado por você.',
      cta: 'DESCOBRIR GRÁTIS',
      image: '/hero-variants/hero-variant-d.webp', // Pessoa confiante
      animationStrategy: 'focused',
    },
    problem: {
      title: 'O método tradicional está errado.',
      subtitle: 'Apostilas, decoreba, aulas chatas. Nada disso funciona. E os números provam.',
    },
    solution: {
      title: 'O anti-método que funciona',
      subtitle: 'Enquanto outros decoram, você responde. Enquanto outros desistem, você avança.',
    },
    finalCta: {
      title: 'Ainda acredita em decoreba?',
      subtitle: 'Ou está pronto para conhecer o método que realmente funciona?',
      cta: 'VER GRÁTIS',
    },
  },

  // E: Emocional (medo → conquista)
  E: {
    hero: {
      headline: 'Do medo da prova à chave na mão',
      subtitle: 'Você não precisa ter medo. O sistema te prepara até você ter certeza absoluta.',
      cta: 'COMEÇAR GRÁTIS',
      image: '/hero-variants/hero-variant-e.webp', // Momento emocional de conquista
      animationStrategy: 'focused',
    },
    problem: {
      title: 'A ansiedade não é sua culpa.',
      subtitle: 'É normal ter medo quando você não sabe se está preparado. O problema é não ter como saber.',
    },
    solution: {
      title: 'Saiba exatamente quando está pronto',
      subtitle: 'Nosso sistema te mostra com precisão quando você pode agendar a prova com confiança.',
    },
    finalCta: {
      title: 'Chega de incerteza.',
      subtitle: 'Comece hoje e saiba exatamente quando estará pronto para passar.',
      cta: 'COMEÇAR GRÁTIS',
    },
  },
}

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
}

const stagger = {
  animate: { transition: { staggerChildren: 0.1 } }
}

// ============================================
// COMPONENTES DE SEÇÃO
// ============================================

// Problem cards data
const problems = [
  { icon: Frown, title: 'Sem direção', desc: 'Você abre o app e não sabe por onde começar. Tenta um pouco de tudo e domina nada.' },
  { icon: Brain, title: 'Esquece rápido', desc: 'Vê hoje, esquece amanhã. Sem repetição inteligente, o conhecimento se perde.' },
  { icon: AlertTriangle, title: 'Atenção dispersa', desc: 'Feeds, notificações, vídeos. Tudo compete pela sua atenção. Preparação perde.' },
  { icon: Clock, title: 'Não sabe se está pronto', desc: 'A ansiedade cresce porque você não tem certeza se pode agendar a prova.' },
]

// Differentiators
const differentiators = [
  { img: '/icon-brain-progress.webp', title: 'O sistema guia seu progresso', desc: 'Você não precisa pensar. Só responder. Nós identificamos suas lacunas.' },
  { img: '/icon-trophy-celebration.webp', title: 'Progresso visível a cada pergunta', desc: 'Você vê exatamente quão perto está da aprovação. Sem achismo.' },
  { img: '/app-mockup-hand.webp', title: 'Gamificação que faz sentido', desc: 'XP, streaks e conquistas que canalizam sua energia para aprender.' },
]

// Steps
const steps = [
  { num: '1', title: 'Crie sua conta', desc: '30 segundos. Sem cartão.' },
  { num: '2', title: 'Faça o diagnóstico', desc: 'Descubra seu nível em 5 minutos.' },
  { num: '3', title: 'Jogue guiado', desc: 'O sistema mostra o que fazer.' },
  { num: '4', title: 'Passe na prova', desc: 'Com confiança e certeza.' },
]

// Testimonials
const testimonials = [
  {
    img: '/testimonial-maria.webp',
    name: 'Maria Santos',
    location: 'São Paulo, SP',
    text: 'Eu tinha reprovado 3 vezes. Achava que era burra pra isso. Com o CNH PASS, joguei 2 semanas e passei com 28 acertos.',
    result: 'Aprovada em 14 dias',
  },
  {
    img: '/testimonial-joao.webp',
    name: 'João Oliveira',
    location: 'Rio de Janeiro, RJ',
    text: 'Trabalho o dia todo e não tinha tempo. A gamificação fez eu jogar no ônibus, no almoço, em todo canto. Resultado: 100% na teórica.',
    result: '30/30 na prova',
  },
  {
    img: '/testimonial-ana.webp',
    name: 'Ana Costa',
    location: 'Belo Horizonte, MG',
    text: 'Tenho TDAH e sempre tive dificuldade pra manter foco. O CNH PASS foi o primeiro método que funcionou pra mim.',
    result: 'Aprovada de primeira',
  },
]

// ============================================
// LANDING PAGE V3
// ============================================
interface LandingPageV3Props {
  variant?: LandingVariant
}

// Export named para uso com props
export function LandingPageV3({ variant = 'A' }: LandingPageV3Props) {
  const content = variants[variant] ?? variants.A // Fallback para A se variante não existe

  return (
    <div className="min-h-screen text-white overflow-x-hidden bg-[#0A0A0A]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b border-white/5 bg-[#0A0A0A]/90">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <LogoInline />
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" size="sm" className="text-neutral-400 hover:text-white hidden sm:flex">
                Entrar
              </Button>
            </Link>
            <Link to="/feed">
              <Button size="sm" className="btn-primary font-bold">
                <Zap className="w-4 h-4 mr-1" />
                Começar Grátis
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* ============================================
          HERO SECTION
          ============================================ */}
      <section className="relative min-h-[100vh] pt-16 overflow-hidden">
        {/* Background */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(ellipse at 50% 0%, rgba(0, 184, 98, 0.08) 0%, transparent 50%),
              radial-gradient(ellipse at 80% 80%, rgba(0, 217, 255, 0.05) 0%, transparent 40%),
              #0A0A0A
            `,
          }}
        />
        {/* Grid removido - ruído visual desnecessário (Ch. 27 Progressive Disclosure) */}

        <div className="relative z-10 max-w-6xl mx-auto px-4 py-16 lg:py-24 flex items-center min-h-[calc(100vh-4rem)]">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16 w-full">
            {/* Text */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="flex-1 max-w-xl text-center lg:text-left"
            >
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black tracking-tight mb-6 leading-[1.1]"
              >
                {String(content.hero.headline).split(' ').map((word, i) =>
                  ['guia', 'liberdade', 'nível', 'medo', 'grátis', 'decorar'].some(w => word.toLowerCase().includes(w)) ? (
                    <span key={i} className="text-brand-green">{word} </span>
                  ) : (
                    <span key={i}>{word} </span>
                  )
                )}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-lg lg:text-xl text-neutral-400 mb-4"
              >
                {content.hero.subtitle}
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-sm lg:text-base text-brand-green/80 mb-8 font-medium"
              >
                Nova lei CNH 2026: curso teórico agora é gratuito pelo governo.
                Você já economizou R$2.400 — invista R$79,90 para garantir aprovação.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-8"
              >
                <Link to="/feed">
                  <button className="btn-engine px-10 py-5 text-lg font-black rounded-2xl flex items-center gap-3 group animate-pulse-glow">
                    <Play className="w-6 h-6 group-hover:scale-110 transition-transform" />
                    {content.hero.cta}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
              </motion.div>

              {/* Trust badges - apenas 2, zero menção a pagamento */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm text-neutral-400"
              >
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-brand-green" />
                  Grátis para começar
                </span>
                <span className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-[#FFB800]" />
                  4.9/5 avaliações
                </span>
              </motion.div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="flex-1 flex justify-center lg:justify-end"
            >
              <div className="relative max-w-[500px]">
                <div className="absolute -inset-8 blur-3xl opacity-30 rounded-3xl bg-brand-green" />
                <img
                  src={content.hero.image}
                  alt="Hero - CNH PASS"
                  className="relative w-full h-auto rounded-3xl"
                  style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.4)' }}
                />

                {/* Badge top-left - Orquestrado por animationStrategy */}
                <motion.div
                  initial={content.hero.animationStrategy === 'reveal' ? { opacity: 0, scale: 0.8 } : { opacity: 1 }}
                  animate={
                    content.hero.animationStrategy === 'default'
                      ? { y: [0, -8, 0] } // Loop infinito (competição visual)
                      : content.hero.animationStrategy === 'sequential'
                      ? { y: [0, -8, 0] } // Loop mas com delay escalonado
                      : content.hero.animationStrategy === 'focused'
                      ? { y: 0 } // Estático - foco no CTA
                      : { opacity: 1, scale: 1 } // reveal - aparece suave
                  }
                  transition={
                    content.hero.animationStrategy === 'default'
                      ? { duration: 2.5, repeat: Infinity }
                      : content.hero.animationStrategy === 'sequential'
                      ? { duration: 2.5, repeat: Infinity, delay: 1.5 } // Espera conteúdo principal
                      : content.hero.animationStrategy === 'focused'
                      ? { duration: 0 } // Sem animação
                      : { delay: 2, duration: 0.6 } // reveal após 2s
                  }
                  className="absolute -top-4 -left-4 glass-card rounded-2xl p-3"
                >
                  <div className="flex items-center gap-3">
                    <img src="/icon-trophy-celebration.webp" alt="" className="w-10 h-10 rounded-xl" />
                    <div>
                      <div className="text-lg font-bold text-brand-green">50.000+</div>
                      <div className="text-[10px] text-neutral-500">Aprovados</div>
                    </div>
                  </div>
                </motion.div>

                {/* Badge 94% REMOVIDO - redundante com 50.000+ (Ch. 19: limite 4 itens) */}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator - estático, sem competir com CTA (Ch. 42) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ delay: 2 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2"
        >
          <ChevronDown className="w-5 h-5 text-neutral-600" />
        </motion.div>
      </section>

      {/* ============================================
          PROBLEM SECTION
          ============================================ */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src="/problem-frustrated-studying.webp" alt="" className="absolute right-0 top-1/2 -translate-y-1/2 w-1/3 opacity-30" />
        </div>

        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-4xl font-black mb-4">{content.problem.title}</h2>
            <p className="text-neutral-400 max-w-2xl mx-auto">{content.problem.subtitle}</p>
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 gap-4"
          >
            {problems.map((p, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="p-6 rounded-2xl border border-white/5 bg-white/[0.02] flex gap-4 hover:border-white/10 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center shrink-0">
                  <p.icon className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="font-bold text-white mb-1">{p.title}</h3>
                  <p className="text-sm text-neutral-500">{p.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-8 text-lg"
          >
            E se existisse um sistema que{' '}
            <span className="text-brand-green font-bold">resolve tudo isso automaticamente</span>?
          </motion.p>
        </div>
      </section>

      {/* ============================================
          SOLUTION SECTION
          ============================================ */}
      <section className="py-20 px-4 bg-[#0d0d0d]">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute -inset-4 blur-2xl opacity-20 bg-brand-green rounded-3xl" />
              <img
                src="/solution-happy-learning.webp"
                alt="Aprendizado gamificado"
                className="relative w-full max-w-md mx-auto rounded-3xl"
                style={{ boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}
              />
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-black mb-4">{content.solution.title}</h2>
              <p className="text-neutral-400 mb-8">{content.solution.subtitle}</p>

              <div className="space-y-6">
                {differentiators.map((d, i) => (
                  <div key={i} className="flex gap-4">
                    <img src={d.img} alt="" className="w-12 h-12 rounded-xl object-cover shrink-0" />
                    <div>
                      <h4 className="font-bold text-white">{d.title}</h4>
                      <p className="text-sm text-neutral-500">{d.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============================================
          STEPS SECTION
          ============================================ */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-2xl md:text-4xl font-black mb-4">4 passos para a aprovação</h2>
            <p className="text-neutral-400">Você só precisa começar. O sistema cuida do resto.</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-brand-green text-black font-black text-2xl flex items-center justify-center mx-auto mb-4 glow-green">
                  {s.num}
                </div>
                <h3 className="font-bold text-white mb-2">{s.title}</h3>
                <p className="text-sm text-neutral-500">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          TESTIMONIALS SECTION
          ============================================ */}
      <section className="py-20 px-4 bg-[#0d0d0d]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-2xl md:text-4xl font-black mb-4">Quem usou, aprovou</h2>
            <p className="text-neutral-400">Histórias reais de pessoas que conquistaram a CNH</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-2xl border border-white/5 bg-white/[0.02]"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-[#FFB800] text-[#FFB800]" />
                  ))}
                </div>

                <p className="text-neutral-300 mb-6 text-sm leading-relaxed">"{t.text}"</p>

                <div className="flex items-center gap-3 mb-4">
                  <img src={t.img} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <p className="font-bold text-white text-sm">{t.name}</p>
                    <p className="text-xs text-neutral-500">{t.location}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-brand-green">
                  <Check className="w-4 h-4" />
                  {t.result}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          FINAL CTA SECTION
          ============================================ */}
      <section id="final-cta" className="py-24 px-4 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: `radial-gradient(ellipse at center, rgba(0,184,98,0.2) 0%, transparent 70%)`,
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-2xl mx-auto text-center"
        >
          <h2 className="text-3xl md:text-5xl font-black mb-6">{content.finalCta.title}</h2>
          <p className="text-lg text-neutral-400 mb-8">{content.finalCta.subtitle}</p>

          <Link to="/feed">
            <button className="btn-engine px-12 py-6 text-xl font-black rounded-2xl flex items-center gap-3 mx-auto group animate-pulse-glow">
              <Play className="w-7 h-7 group-hover:scale-110 transition-transform" />
              {content.finalCta.cta}
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </button>
          </Link>

          <p className="text-sm text-neutral-500 mt-6 flex items-center justify-center gap-4 flex-wrap">
            <span className="flex items-center gap-2">
              <Shield className="w-4 h-4" /> Dados seguros
            </span>
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4" /> Sem cartão
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" /> Cancele quando quiser
            </span>
          </p>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-white/5">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex flex-wrap justify-center gap-6 mb-6 text-sm text-neutral-500">
            <span className="flex items-center gap-2">
              <Shield className="w-4 h-4" /> SSL Seguro
            </span>
            <span className="flex items-center gap-2">
              <Heart className="w-4 h-4" /> Garantia 7 dias
            </span>
            <span className="flex items-center gap-2">
              <Star className="w-4 h-4" /> 4.9 no Google
            </span>
            <span className="flex items-center gap-2">
              <Users className="w-4 h-4" /> +50.000 aprovados
            </span>
          </div>
          <p className="text-xs text-neutral-600">© 2025 CNH PASS. Todos os direitos reservados.</p>
        </div>
      </footer>

      {/* Floating CTA - Mobile only (neuro-UX: progresso + checkpoints) */}
      {/* FIXME: FloatingCTA component missing
      <FloatingCTA
        textoAcao={content.hero.cta}
        mostrarProgresso={true}
        progressoInicial={10} // Entrou no site = 10%, resto é na plataforma
        targetSectionId="final-cta"
      />
      */}

      {/* A/B Variant Indicator (dev only) */}
      {import.meta.env.DEV && (
        <div className="fixed top-20 right-4 bg-black/90 text-white text-xs px-3 py-2 rounded-lg font-mono z-50 border border-white/10">
          <div className="text-brand-green font-bold mb-1">Landing V3</div>
          <div>Variante: <span className="text-brand-cyan">{variant}</span></div>
        </div>
      )}
    </div>
  )
}

export default LandingPageV3
